import { useId } from 'react';

/**
 * Charts drawn as inline SVG.
 *
 * No charting library: the three shapes needed here are a stacked bar, a
 * sparkline and a donut, and the smallest credible library is ~50KB gzipped —
 * a third of the entire budget for something a hundred lines of SVG covers.
 * These also inherit the theme tokens, which a library would fight.
 */

export interface SeriesPoint {
  day: string;
  inbound: number;
  outbound: number;
  internal: number;
  answered: number;
  total: number;
  duration_sec: number;
  spend: string;
}

const fmtDay = (iso: string): string =>
  new Date(iso + 'T00:00:00Z').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });

/**
 * Stacked daily call volume.
 *
 * Stacked rather than grouped: the question is "how busy were we", with the
 * mix as secondary. Grouped bars answer neither well at 30 points.
 */
export function VolumeChart({ data, height = 150 }: { data: SeriesPoint[]; height?: number }) {
  const id = useId();
  const max = Math.max(1, ...data.map((d) => d.inbound + d.outbound + d.internal));
  const n = Math.max(1, data.length);
  const gap = 2;
  const bw = Math.max(2, (100 - gap * (n - 1)) / n);

  const bars = data.map((d, i) => {
    const x = i * (bw + gap);
    const total = d.inbound + d.outbound + d.internal;
    let y = 100;
    const seg = (v: number, fill: string, key: string) => {
      if (v === 0) return null;
      const h = (v / max) * 100;
      y -= h;
      return <rect key={key} x={x} y={y} width={bw} height={h} fill={fill} rx={0.6} />;
    };
    return (
      <g key={d.day}>
        <title>{`${fmtDay(d.day)} — ${total} calls (${d.inbound} in, ${d.outbound} out, ${d.internal} internal)`}</title>
        {seg(d.internal, 'var(--arc-fg-4)', 'i')}
        {seg(d.outbound, 'var(--arc-dir-out)', 'o')}
        {seg(d.inbound, 'var(--arc-dir-in)', 'n')}
        {total === 0 && <rect x={x} y={99.4} width={bw} height={0.6} fill="var(--arc-border)" />}
      </g>
    );
  });

  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
        role="img" aria-labelledby={`${id}-t`}>
        <title id={`${id}-t`}>Daily call volume over {data.length} days</title>
        {bars}
      </svg>
      <div className="arc-row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span className="arc-subtle" style={{ fontSize: 11 }}>
          {data[0] ? fmtDay(data[0].day) : ''}
        </span>
        <div className="arc-row" style={{ gap: 12 }}>
          <Legend colour="var(--arc-dir-in)" label="inbound" />
          <Legend colour="var(--arc-dir-out)" label="outbound" />
          <Legend colour="var(--arc-fg-4)" label="internal" />
        </div>
        <span className="arc-subtle" style={{ fontSize: 11 }}>
          {data.length ? fmtDay(data[data.length - 1]!.day) : ''}
        </span>
      </div>
    </div>
  );
}

export function Legend({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="arc-row" style={{ gap: 4, fontSize: 11 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: colour }} aria-hidden="true" />
      <span className="arc-subtle">{label}</span>
    </span>
  );
}

/** A single-series line, for spend or duration trends. */
export function Sparkline({ values, height = 44, colour = 'var(--arc-accent)', label }: {
  values: number[]; height?: number; colour?: string;
  /** What the line is, for the accessible title. Defaults to a daily trend. */
  label?: string;
}) {
  const id = useId();
  if (values.length < 2) {
    return <div style={{ height }} className="arc-subtle">not enough data yet</div>;
  }
  const max = Math.max(1, ...values);
  const step = 100 / (values.length - 1);
  const pts = values.map((v, i) => `${i * step},${100 - (v / max) * 92}`);

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
      role="img" aria-labelledby={`${id}-t`}>
      <title id={`${id}-t`}>{label ? `${label}, peak ${max}` : `Trend over ${values.length} days, peak ${max}`}</title>
      {/* Fill first so the stroke sits on top of its own shading. */}
      <polygon points={`0,100 ${pts.join(' ')} 100,100`} fill={colour} opacity={0.12} />
      <polyline points={pts.join(' ')} fill="none" stroke={colour}
        strokeWidth={1.6} vectorEffect="non-scaling-stroke"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Uptime as a row of bars, one per interval, oldest first -- the status-page
 * pattern, because it answers "when" as well as "how much" at a glance.
 *
 * Colour follows the status tokens: all checks answered is ok, most is warn,
 * fewer is danger. An interval with no checks is drawn empty rather than
 * dropped, so a gap in the record reads as a gap, not as a shorter day.
 */
export function UptimeStrip({ buckets, height = 26 }: {
  buckets: Array<{ at: string; up_pct: number | null }>;
  height?: number;
}) {
  const id = useId();
  const n = buckets.length;
  if (n === 0) return <div style={{ height }} className="arc-subtle">no record yet</div>;
  const colour = (p: number | null) =>
    p === null ? 'var(--arc-surface-3)'
      : p >= 100 ? 'var(--arc-ok)'
      : p >= 90 ? 'var(--arc-warn)' : 'var(--arc-danger)';
  const measured = buckets.filter((b) => b.up_pct !== null);
  const worst = measured.length ? Math.min(...measured.map((b) => b.up_pct!)) : null;
  return (
    <svg viewBox={`0 0 ${n * 4} ${height}`} preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
      role="img" aria-labelledby={`${id}-t`}>
      <title id={`${id}-t`}>
        {worst === null ? 'No checks in this period' : `Uptime per interval, lowest ${worst}%`}
      </title>
      {buckets.map((b, i) => (
        <rect key={b.at} x={i * 4} y={0} width={3} height={height} rx={0.8} fill={colour(b.up_pct)}>
          <title>{`${new Date(b.at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })} · ${b.up_pct === null ? 'no checks' : `${b.up_pct}% up`}`}</title>
        </rect>
      ))}
    </svg>
  );
}

/** Answer-rate donut. Reads at a glance; the number is inside it. */
export function Donut({ value, label, size = 92 }: {
  value: number; label: string; size?: number;
}) {
  const pct = Math.max(0, Math.min(100, value));
  const r = 15.9155;
  const tone = pct >= 70 ? 'var(--arc-ok)' : pct >= 40 ? 'var(--arc-warn)' : 'var(--arc-danger)';
  return (
    <div style={{ display: 'grid', placeItems: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 36 36" role="img" aria-label={`${label}: ${pct}%`}>
        <circle cx="18" cy="18" r={r} fill="none" stroke="var(--arc-surface-3)" strokeWidth="3.2" />
        <circle
          cx="18" cy="18" r={r} fill="none" stroke={tone} strokeWidth="3.2"
          strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset="25"
          strokeLinecap="round" transform="rotate(-90 18 18)"
        />
        <text x="18" y="18.2" textAnchor="middle" dominantBaseline="middle"
          style={{ font: '600 7px var(--arc-font)', fill: 'var(--arc-fg)' }}>
          {pct}%
        </text>
      </svg>
      <span className="arc-subtle" style={{ fontSize: 11, marginTop: 4 }}>{label}</span>
    </div>
  );
}

/**
 * A multi-segment donut.
 *
 * Distinct from `Donut` above, which is a single value against 100 — a
 * completion ring. This one divides a total between categories, which is a
 * different question and needs a different shape.
 *
 * Drawn with stroke-dasharray on one circle rather than as arc paths: the
 * circumference is normalised to 100 by choosing r = 100/2π, so a segment's
 * dash length IS its percentage and there is no trigonometry to get wrong.
 */
export function PieDonut({ data, size = 160 }: {
  data: Array<{ label: string; value: number; color: string }>;
  size?: number;
}) {
  const total = data.reduce((n, d) => n + d.value, 0);
  if (total <= 0) {
    return (
      <div style={{ width: size, height: size, display: 'grid', placeItems: 'center' }}>
        <span className="arc-subtle" style={{ fontSize: 12 }}>No spend</span>
      </div>
    );
  }

  const r = 15.9155;   // circumference = 100
  /**
   * Start the ring at 12 o'clock, ONCE.
   *
   * A circle's stroke starts at 3 o'clock, and there are two ways to move it:
   * rotate the element, or offset the dash pattern. Doing both turns the chart
   * a further quarter and the first slice lands at 9 o'clock. The rotation is
   * kept because it reads as what it is; the dash offset now only tracks where
   * each segment begins.
   *
   * 100 rather than 0 so every offset stays positive -- a negative
   * stroke-dashoffset is an error in SVG 1.1, and one full circumference is
   * the same place as none.
   */
  let offset = 100;

  return (
    <svg width={size} height={size} viewBox="0 0 36 36" role="img"
      aria-label={data.map((d) => `${d.label} ${Math.round((d.value / total) * 100)}%`).join(', ')}>
      <circle cx="18" cy="18" r={r} fill="none"
        stroke="var(--arc-surface-3)" strokeWidth="4" />
      {data.map((d) => {
        const pct = (d.value / total) * 100;
        // Segments smaller than a hairline still get drawn: dropping them
        // would make the ring not add up, which is worse than a sliver.
        const seg = (
          <circle key={d.label} cx="18" cy="18" r={r} fill="none"
            stroke={d.color} strokeWidth="4"
            strokeDasharray={`${pct} ${100 - pct}`}
            strokeDashoffset={String(offset)}
            transform="rotate(-90 18 18)" />
        );
        offset -= pct;
        return seg;
      })}
    </svg>
  );
}

/**
 * Answered against abandoned, bucket by bucket.
 *
 * Stacked rather than side by side, because the height then reads as "how many
 * people called" and the red portion as "how many gave up" -- one shape, two
 * facts. Paired bars make the eye do the addition to get the same answer.
 *
 * Buckets arrive pre-labelled: whether an hour or a day is the right grain
 * depends on the window being reported, and that is the caller's decision, not
 * the chart's.
 */
export function QueueVolumeChart({ data, height = 140 }: {
  data: Array<{ label: string; answered: number; abandoned: number }>;
  height?: number;
}) {
  const id = useId();
  const max = Math.max(1, ...data.map((d) => d.answered + d.abandoned));
  const n = Math.max(1, data.length);
  const gap = data.length > 60 ? 0.4 : 2;
  const bw = Math.max(0.6, (100 - gap * (n - 1)) / n);

  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
        role="img" aria-labelledby={`${id}-t`}>
        <title id={`${id}-t`}>Calls answered and abandoned across {data.length} buckets</title>
        {data.map((d, i) => {
          const x = i * (bw + gap);
          const total = d.answered + d.abandoned;
          const hAns = (d.answered / max) * 100;
          const hAbn = (d.abandoned / max) * 100;
          return (
            <g key={d.label}>
              <title>{`${d.label} — ${total} offered, ${d.answered} answered, ${d.abandoned} abandoned`}</title>
              {d.abandoned > 0 && (
                <rect x={x} y={100 - hAns - hAbn} width={bw} height={hAbn}
                  fill="var(--arc-danger)" rx={0.6} />
              )}
              {d.answered > 0 && (
                <rect x={x} y={100 - hAns} width={bw} height={hAns}
                  fill="var(--arc-ok)" rx={0.6} />
              )}
              {/* An empty bucket gets a baseline tick rather than nothing, so a
                  quiet hour is visibly quiet instead of looking like missing
                  data. */}
              {total === 0 && (
                <rect x={x} y={99.4} width={bw} height={0.6} fill="var(--arc-border)" />
              )}
            </g>
          );
        })}
      </svg>
      <div className="arc-row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span className="arc-subtle" style={{ fontSize: 11 }}>{data[0]?.label ?? ''}</span>
        <div className="arc-row" style={{ gap: 12 }}>
          <Legend colour="var(--arc-ok)" label="answered" />
          <Legend colour="var(--arc-danger)" label="abandoned" />
        </div>
        <span className="arc-subtle" style={{ fontSize: 11 }}>
          {data.length ? data[data.length - 1]!.label : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * Revenue against cost, per day. One bar per day is what was sold; the darker foot of it is what
 * the carrier charged us, so the lighter top IS the margin -- the question the chart answers,
 * read without arithmetic. A day sold at a loss shows cost over the bar's height in the danger
 * colour, since that is the day worth a look.
 */
export function MarginChart({ data, height = 150, currency }: {
  data: Array<{ day: string; sell: number; cost: number }>; height?: number; currency: string;
}) {
  const id = useId();
  const max = Math.max(1, ...data.map((d) => Math.max(d.sell, d.cost)));
  const n = Math.max(1, data.length);
  const gap = 2;
  const bw = Math.max(2, (100 - gap * (n - 1)) / n);
  const fmt = (v: number) => `${currency} ${v.toFixed(2)}`;
  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
        role="img" aria-labelledby={`${id}-t`}>
        <title id={`${id}-t`}>Daily revenue and carrier cost over {data.length} days</title>
        {data.map((d, i) => {
          const x = i * (bw + gap);
          const hSell = (d.sell / max) * 100;
          const hCost = (d.cost / max) * 100;
          const loss = d.cost > d.sell;
          return (
            <g key={d.day}>
              <title>{`${fmtDay(d.day)} — sold ${fmt(d.sell)}, cost ${fmt(d.cost)}, margin ${fmt(d.sell - d.cost)}`}</title>
              {hSell > 0 && <rect x={x} y={100 - hSell} width={bw} height={hSell} fill="var(--arc-c2)" opacity={0.45} rx={0.6} />}
              {hCost > 0 && <rect x={x} y={100 - hCost} width={bw} height={hCost} fill={loss ? 'var(--arc-danger)' : 'var(--arc-c2)'} rx={0.6} />}
              {d.sell === 0 && d.cost === 0 && <rect x={x} y={99.4} width={bw} height={0.6} fill="var(--arc-border)" />}
            </g>
          );
        })}
      </svg>
      <div className="arc-row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span className="arc-subtle" style={{ fontSize: 11 }}>{data[0] ? fmtDay(data[0].day) : ''}</span>
        <div className="arc-row" style={{ gap: 12 }}>
          <Legend colour="color-mix(in srgb, var(--arc-c2) 45%, transparent)" label="margin" />
          <Legend colour="var(--arc-c2)" label="carrier cost" />
          <Legend colour="var(--arc-danger)" label="sold at a loss" />
        </div>
        <span className="arc-subtle" style={{ fontSize: 11 }}>{data.length ? fmtDay(data[data.length - 1]!.day) : ''}</span>
      </div>
    </div>
  );
}

/**
 * One count per day, up to two series stacked: the shape for "how often did this happen" over a
 * window -- refusals at the edge, holds placed. Every day gets a bar, an empty one a baseline tick,
 * so a quiet week reads as quiet and not as missing. The labels under it are the first and last
 * day; a legend only when there is a second series to tell apart.
 */
export function DayBars({ data, height = 110, series }: {
  data: Array<{ day: string; a: number; b?: number }>;
  height?: number;
  series: { a: { label: string; colour: string }; b?: { label: string; colour: string } };
}) {
  const id = useId();
  const max = Math.max(1, ...data.map((d) => d.a + (d.b ?? 0)));
  const n = Math.max(1, data.length);
  const gap = n > 14 ? 1.2 : 3;
  const bw = Math.max(1, (100 - gap * (n - 1)) / n);
  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
        role="img" aria-labelledby={`${id}-t`}>
        <title id={`${id}-t`}>{series.a.label}{series.b ? ` and ${series.b.label}` : ''} per day over {data.length} days</title>
        {data.map((d, i) => {
          const x = i * (bw + gap);
          const b = d.b ?? 0;
          const hA = (d.a / max) * 100;
          const hB = (b / max) * 100;
          return (
            <g key={d.day}>
              <title>{`${fmtDay(d.day)} — ${d.a} ${series.a.label}${series.b ? `, ${b} ${series.b.label}` : ''}`}</title>
              {hB > 0 && <rect x={x} y={100 - hA - hB} width={bw} height={hB} fill={series.b?.colour} rx={0.6} />}
              {hA > 0 && <rect x={x} y={100 - hA} width={bw} height={hA} fill={series.a.colour} rx={0.6} />}
              {d.a + b === 0 && <rect x={x} y={99.4} width={bw} height={0.6} fill="var(--arc-border)" />}
            </g>
          );
        })}
      </svg>
      <div className="arc-row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span className="arc-subtle" style={{ fontSize: 11 }}>{data[0] ? fmtDay(data[0].day) : ''}</span>
        <div className="arc-row" style={{ gap: 12 }}>
          <Legend colour={series.a.colour} label={series.a.label} />
          {series.b && <Legend colour={series.b.colour} label={series.b.label} />}
        </div>
        <span className="arc-subtle" style={{ fontSize: 11 }}>{data.length ? fmtDay(data[data.length - 1]!.day) : ''}</span>
      </div>
    </div>
  );
}

/**
 * Calls by hour of the day, answered at the foot of each bar.
 *
 * Twenty-four bars whatever the window: the question is "when are we busy", which a day-by-day
 * chart cannot answer. Hours are labelled every six so the axis reads without crowding.
 */
export function HourChart({ data, height = 130 }: {
  data: Array<{ hour: number; total: number; answered: number }>;
  height?: number;
}) {
  const id = useId();
  const max = Math.max(1, ...data.map((d) => d.total));
  const gap = 1.2;
  const bw = (100 - gap * 23) / 24;
  const hh = (h: number) => String(h).padStart(2, '0');
  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
        role="img" aria-labelledby={`${id}-t`}>
        <title id={`${id}-t`}>Calls by hour of the day</title>
        {data.map((d) => {
          const x = d.hour * (bw + gap);
          const hAns = (d.answered / max) * 100;
          const hRest = ((d.total - d.answered) / max) * 100;
          return (
            <g key={d.hour}>
              <title>{`${hh(d.hour)}:00 — ${d.total} calls, ${d.answered} answered`}</title>
              {hRest > 0 && <rect x={x} y={100 - hAns - hRest} width={bw} height={hRest} fill="var(--arc-fg-4)" opacity={0.45} rx={0.6} />}
              {hAns > 0 && <rect x={x} y={100 - hAns} width={bw} height={hAns} fill="var(--arc-c1)" rx={0.6} />}
              {d.total === 0 && <rect x={x} y={99.4} width={bw} height={0.6} fill="var(--arc-border)" />}
            </g>
          );
        })}
      </svg>
      <div className="arc-row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        {[0, 6, 12, 18, 23].map((h) => (
          <span key={h} className="arc-subtle arc-num" style={{ fontSize: 11 }}>{hh(h)}:00</span>
        ))}
      </div>
      <div className="arc-row" style={{ gap: 12, marginTop: 4 }}>
        <Legend colour="var(--arc-c1)" label="answered" />
        <Legend colour="color-mix(in srgb, var(--arc-fg-4) 45%, transparent)" label="not answered" />
      </div>
    </div>
  );
}
